import {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from "react";
import { Box, Snackbar, Alert, Button, CircularProgress } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmationPanel from "../../confirmationPanel";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import config from "../../../config";
import { bindActionCreators } from "../../../actions/actionCreators";
import * as userActions from "../../../actions/userActions";
import { UserActions } from "../../../actions/userActions";
import * as listActions from "../../../actions/listActions";
import { ListActions } from "../../../actions/listActions";
import * as eventActions from "../../../actions/eventActions";
import { EventActions } from "../../../actions/eventActions";
import { AppContext } from "../../../models/applicationState";
import { Context } from "../../context";
import {
  CalendarConnection,
  Meeting,
  MeetingStatus,
  Session,
  SessionStatus,
} from "../../../models";
import { CalendarEvent } from "../../../models/calendarEvent";
import { CoacheeService } from "../../../services/coachingNavigator/coacheeService";
import { SessionService } from "../../../services/coachingNavigator/sessionService";
import {
  mapGoogleEvent as mapGoogleEventBase,
  mapOutlookEvent as mapOutlookEventBase,
} from "../../../util/calendarEvent";
import { useCalendarEvents } from "../../../hooks/useCalendarEvents";
import MonthlyCalendarView from "../../calendar/MonthlyCalendarView";
import MeetingDetailsDialog from "../../calendar/MeetingDetailsDialog";
import { CoachingClient, LinkClientDialog } from "./index";

const coacheeService = new CoacheeService(config.api.baseUrl, "/coachee");
const sessionService = new SessionService(config.api.baseUrl, "/sessions");

const SEARCH_PAST_DAYS = 365 * 5;
const SEARCH_FUTURE_DAYS = 365 * 5;

type SessionWithMongoId = Session & { _id?: unknown };

const toTime = (value?: Date | string): number => {
  if (!value) return 0;
  const parsed = new Date(value);
  const time = parsed.getTime();
  return Number.isNaN(time) ? 0 : time;
};

const sessionRecency = (session: SessionWithMongoId): number =>
  Math.max(
    toTime(session.updatedDate),
    toTime(session.createdDate),
    toTime(session.startTime),
  );

const resolveSessionId = (session?: SessionWithMongoId): string | undefined => {
  if (!session) return undefined;
  const candidate = session.id ?? session._id;
  if (!candidate) return undefined;
  if (typeof candidate === "string") return candidate;
  return String(candidate);
};

const buildExistingSessionByEventId = (
  sessions: Session[],
): Map<string, SessionWithMongoId> => {
  const map = new Map<string, SessionWithMongoId>();
  (sessions || []).forEach((rawSession) => {
    const session = rawSession as SessionWithMongoId;
    const eventId = session.calendarEventId;
    if (session.archived || !eventId) return;
    const existing = map.get(eventId);
    if (!existing || sessionRecency(session) >= sessionRecency(existing)) {
      map.set(eventId, session);
    }
  });
  return map;
};

const EventList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const appContext = useContext<AppContext>(Context);

  const actions = useMemo(
    () => ({
      users: bindActionCreators(
        userActions,
        appContext.dispatch,
      ) as unknown as UserActions,
      lists: bindActionCreators(
        listActions,
        appContext.dispatch,
      ) as unknown as ListActions,
      events: bindActionCreators(
        eventActions,
        appContext.dispatch,
      ) as unknown as EventActions,
    }),
    [appContext.dispatch],
  );

  const rawConnections = appContext.state.invoker?.calendarConnections ?? [];
  const connectionsKey = JSON.stringify(
    rawConnections.map((c) => `${c.provider}:${c.accountUsername}`),
  );
  const connections: CalendarConnection[] = useMemo(
    () => rawConnections,
    [connectionsKey], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const creatorEmail = (appContext.state.invoker?.email || "").trim();

  // ── Coaching-specific state ───────────────────────────────────────
  const [clients, setClients] = useState<CoachingClient[]>([]);
  const [linkedClientByEventId, setLinkedClientByEventId] = useState<
    Record<string, string>
  >({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [linkError, setLinkError] = useState<string | null>(null);
  const [isLinking, setIsLinking] = useState(false);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [includePastInSearch, setIncludePastInSearch] = useState(false);
  const [disconnectEventId, setDisconnectEventId] = useState<string | null>(
    null,
  );
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [detailEvent, setDetailEvent] = useState<CalendarEvent | null>(null);

  // ── Meeting navigation helpers (chevron: create / open meeting notes) ──
  const formatEventTime = (value?: Date | string): string => {
    if (!value) return "";
    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return new Intl.DateTimeFormat(i18next.language || undefined, {
      hour: "2-digit",
      minute: "2-digit",
    }).format(parsed);
  };

  const bucketMeetingsByEventId = (mList: Meeting[]) =>
    (mList || []).reduce(
      (acc, m) => {
        if (m.eventId) acc[m.eventId] = m;
        return acc;
      },
      {} as Record<string, Meeting>,
    );

  const [meetings, setMeetings] = useState(() =>
    bucketMeetingsByEventId(appContext.state.lists),
  );

  useEffect(() => {
    setMeetings(bucketMeetingsByEventId(appContext.state.lists));
  }, [appContext.state.lists]); // eslint-disable-line react-hooks/exhaustive-deps

  const mergeMeetingWithEvent = (
    meeting: Meeting,
    event: CalendarEvent,
  ): Meeting => ({
    ...meeting,
    date: meeting.date || event.startDate,
    startTime:
      meeting.startTime || formatEventTime(event.startDate) || undefined,
    endTime: meeting.endTime || formatEventTime(event.endDate) || undefined,
    participants:
      meeting.participants && meeting.participants.length > 0
        ? meeting.participants
        : event.invitees,
    eventId: meeting.eventId || event.id,
    calendarOrganizerEmail: meeting.calendarOrganizerEmail || event.creator,
    teamsJoinWebUrl: meeting.teamsJoinWebUrl || event.onlineMeetingUrl,
  });

  const inFlightEventIds = useRef<Set<string>>(new Set());

  const onNavigate = useCallback(
    (meeting: Meeting, event?: CalendarEvent) => {
      if (!meeting) return;
      if (event) actions.events.select(event);
      actions.lists.select(meeting);
      if (meeting.status === MeetingStatus.Published) {
        navigate(`/library/${meeting.id}/published`, {
          state: { blockPrevious: true },
        });
        return;
      }
      navigate(`/meetings/workflow/${meeting.id}`, {
        state: { blockPrevious: true },
      });
    },
    [actions.events, actions.lists, navigate],
  );

  const onChevronClickNavigate = useCallback(
    async (event: CalendarEvent) => {
      const existingMeeting = meetings[event.id!];
      if (existingMeeting) {
        const synced = mergeMeetingWithEvent(existingMeeting, event);
        const needsSync =
          (!existingMeeting.date && !!event.startDate) ||
          (!existingMeeting.startTime && !!synced.startTime) ||
          (!existingMeeting.endTime && !!synced.endTime) ||
          (!existingMeeting.eventId && !!event.id) ||
          ((!existingMeeting.participants ||
            existingMeeting.participants.length === 0) &&
            !!event.invitees?.length) ||
          (!existingMeeting.calendarOrganizerEmail && !!event.creator) ||
          (!existingMeeting.teamsJoinWebUrl && !!event.onlineMeetingUrl);
        const meetingToOpen = needsSync
          ? await actions.lists.save(synced)
          : existingMeeting;
        onNavigate(meetingToOpen, event);
        return;
      }
      if (event.id && inFlightEventIds.current.has(event.id)) return;
      if (event.id) inFlightEventIds.current.add(event.id);
      try {
        const saved = await actions.lists.save({
          name: event.name,
          date: event.startDate,
          startTime: formatEventTime(event.startDate) || undefined,
          endTime: formatEventTime(event.endDate) || undefined,
          participants: event.invitees,
          eventId: event.id,
          calendarOrganizerEmail: event.creator,
          teamsJoinWebUrl: event.onlineMeetingUrl,
        });
        onNavigate(saved, event);
      } finally {
        if (event.id) inFlightEventIds.current.delete(event.id);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meetings, actions.lists, onNavigate],
  );
  // calendarEventId stays compatible with existing Session records.
  const mapOutlookEventCoaching = useCallback(
    (raw: unknown, accountUsername?: string): CalendarEvent =>
      mapOutlookEventBase(raw, accountUsername, {
        namespaceId: true,
        includeLocation: true,
      }),
    [],
  );
  const mapGoogleEventCoaching = useCallback(
    (raw: unknown, accountUsername?: string): CalendarEvent =>
      mapGoogleEventBase(raw, accountUsername, {
        namespaceId: true,
        includeLocation: true,
      }),
    [],
  );
  const matchEvent = useCallback(
    (event: CalendarEvent, lower: string): boolean => {
      if (event.name?.toLowerCase().includes(lower)) return true;
      if (event.creator?.toLowerCase().includes(lower)) return true;
      if (event.invitees?.some((i) => i.toLowerCase().includes(lower)))
        return true;
      const id = event.id || event.uid || "";
      const linked = linkedClientByEventId[id];
      if (linked && linked.toLowerCase().includes(lower)) return true;
      return false;
    },
    [linkedClientByEventId],
  );

  const refreshInvoker = useCallback(
    () => actions.users.getInvoker(),
    [actions.users],
  );

  const calendar = useCalendarEvents<CalendarEvent>({
    connections,
    refreshInvoker,
    mapOutlookEvent: mapOutlookEventCoaching,
    mapGoogleEvent: mapGoogleEventCoaching,
    matchEvent,
    t,
    searchPastDays: includePastInSearch ? SEARCH_PAST_DAYS : 0,
    searchFutureDays: SEARCH_FUTURE_DAYS,
    restoreOriginalAccountOnRedirect: true,
    showConnectionSnackbars: true,
  });

  const {
    events,
    filteredEvents,
    connectionStatuses,
    searchText,
    setSearchText,
    isSearchActive,
    searchInProgress,
    loading,
    viewedMonth,
    setViewedMonth,
    handlePrevMonth,
    handleNextMonth,
    availableMonths,
    connectLoading,
    connectError,
    setConnectError,
    handleConnectCalendar,
    handleDisconnectAccount,
    fetchAllEvents,
    snackbar,
    setSnackbar,
  } = calendar;

  // ── Load coachees ─────────────────────────────────────────────────
  useEffect(() => {
    if (!creatorEmail) {
      setClients([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const coachees = await coacheeService.getByCreator(creatorEmail);
        if (cancelled) return;
        const mapped = (coachees || [])
          .filter((coachee) => coachee.active !== false)
          .map((coachee) => ({
            id: (coachee.id || "").toString(),
            name: coachee.name,
            company: coachee.company || "-",
          }))
          .filter((coachee) => !!coachee.id);
        setClients(mapped);
      } catch {
        if (!cancelled) {
          setLinkError(
            t("coachingNavigator.coachingCalendar.failedToLoadClients"),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [creatorEmail, t]);

  // ── Hydrate linkedClientByEventId from existing sessions ──────────
  useEffect(() => {
    if (!creatorEmail || events.length === 0 || clients.length === 0) {
      setLinkedClientByEventId({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const sessions = await sessionService.getByCreator(creatorEmail);
        if (cancelled) return;
        const clientNameById = new Map(
          clients.map((client) => [client.id, client.name]),
        );
        const sessionByEventId = buildExistingSessionByEventId(sessions || []);
        const next: Record<string, string> = {};
        events.forEach((event) => {
          const eventId = event.id || event.uid;
          if (!eventId) return;
          const session = sessionByEventId.get(eventId);
          const clientName = session?.clientId
            ? clientNameById.get(session.clientId)
            : undefined;
          if (clientName) next[eventId] = clientName;
        });
        setLinkedClientByEventId(next);
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load linked clients from sessions:", error);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [creatorEmail, events, clients]);

  // ── Selection / linkage handlers ──────────────────────────────────
  const handleSelectionChange = useCallback((id: string, checked: boolean) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleSelectAllVisible = useCallback(
    (checked: boolean) => {
      if (!checked) {
        setSelectedIds(new Set());
        return;
      }
      setSelectedIds(
        new Set(
          filteredEvents
            .map((event) => event.id || event.uid || "")
            .filter(Boolean),
        ),
      );
    },
    [filteredEvents],
  );

  const applyLinkage = useCallback((clientName: string, ids: Set<string>) => {
    setLinkedClientByEventId((previous) => {
      const next = { ...previous };
      ids.forEach((id) => {
        next[id] = clientName;
      });
      return next;
    });
    setSelectedIds(new Set());
  }, []);

  const getSelectedEvents = useCallback((): CalendarEvent[] => {
    return events.filter((event) => {
      const id = event.id || event.uid || "";
      return id && selectedIds.has(id);
    });
  }, [events, selectedIds]);

  const upsertSessionFromEvent = useCallback(
    async (
      event: CalendarEvent,
      clientId: string,
      existingSessionByEventId: Map<string, SessionWithMongoId>,
    ) => {
      const eventId = event.id || event.uid || "";
      const start = event.startDate ? new Date(event.startDate) : new Date();
      const end = event.endDate
        ? new Date(event.endDate)
        : new Date(start.getTime() + 60 * 60000);
      const duration = Math.max(
        5,
        Math.round((end.getTime() - start.getTime()) / 60000),
      );

      const existingSession = existingSessionByEventId.get(eventId);
      const existingSessionId = resolveSessionId(existingSession);
      if (existingSessionId) {
        const updated = await sessionService.save({
          ...existingSession,
          id: existingSessionId,
          name: event.name,
          calendarEventId: eventId,
          clientId,
          startTime: start,
          endTime: end,
          duration,
          creator: creatorEmail || existingSession?.creator,
          status: existingSession?.status || SessionStatus.Planned,
        });
        existingSessionByEventId.set(eventId, updated);
        return updated;
      }

      try {
        const created = await sessionService.save({
          name: event.name,
          calendarEventId: eventId,
          clientId,
          startTime: start,
          endTime: end,
          duration,
          status: SessionStatus.Planned,
          creator: creatorEmail || undefined,
        });
        existingSessionByEventId.set(eventId, created);
        return created;
      } catch (error: any) {
        if (error?.response?.status !== 409 || !creatorEmail) {
          throw error;
        }
        const sessions = await sessionService.getByCreator(creatorEmail);
        const matched = buildExistingSessionByEventId(sessions || []).get(
          eventId,
        );
        const matchedId = resolveSessionId(matched);
        if (!matched || !matchedId) throw error;
        const updated = await sessionService.save({
          ...matched,
          id: matchedId,
          name: event.name,
          calendarEventId: eventId,
          clientId,
          startTime: start,
          endTime: end,
          duration,
          creator: creatorEmail || matched.creator,
          status: matched.status || SessionStatus.Planned,
        });
        existingSessionByEventId.set(eventId, updated);
        return updated;
      }
    },
    [creatorEmail],
  );

  const linkEventsToClient = useCallback(
    async (clientId: string, clientName: string) => {
      const selectedEvents = getSelectedEvents();
      if (selectedEvents.length === 0) return;
      if (!creatorEmail) {
        setLinkError(
          t("coachingNavigator.coachingCalendar.failedToLinkEvents") ||
          "Failed to link events to client",
        );
        return;
      }

      setLinkError(null);
      setIsLinking(true);

      try {
        const existingSessions =
          await sessionService.getByCreator(creatorEmail);
        const existingSessionByEventId = buildExistingSessionByEventId(
          existingSessions || [],
        );

        if (selectedEvents.length === 1) {
          try {
            await upsertSessionFromEvent(
              selectedEvents[0],
              clientId,
              existingSessionByEventId,
            );
            const onlyId = selectedEvents[0].id || selectedEvents[0].uid || "";
            applyLinkage(clientName, new Set([onlyId]));
          } catch (error: any) {
            const backendMessage =
              error?.response?.data?.message || error?.message || "";
            setLinkError(
              backendMessage ||
              t("coachingNavigator.coachingCalendar.failedToLinkEvent") ||
              "Failed to link event to client",
            );
            // Re-throw so LinkClientDialog can surface failure in the progress dialog.
            throw error;
          }
          return;
        }

        for (const event of selectedEvents) {
          await upsertSessionFromEvent(
            event,
            clientId,
            existingSessionByEventId,
          );
        }

        applyLinkage(
          clientName,
          new Set(
            selectedEvents
              .map((event) => event.id || event.uid || "")
              .filter(Boolean),
          ),
        );
      } catch (error: any) {
        const backendMessage =
          error?.response?.data?.message || error?.message || "";
        setLinkError(
          backendMessage ||
          t("coachingNavigator.coachingCalendar.failedToLinkEvents"),
        );
        // Re-throw so LinkClientDialog can surface failure in the progress dialog.
        throw error;
      } finally {
        setIsLinking(false);
      }
    },
    [
      applyLinkage,
      creatorEmail,
      getSelectedEvents,
      navigate,
      t,
      upsertSessionFromEvent,
    ],
  );

  const handleClientSelected = async (clientId: string) => {
    const client = clients.find((item) => item.id === clientId);
    if (!client) return;
    setIsLinkDialogOpen(false);
    // Await so any rejection propagates to LinkClientDialog's onLink Promise chain.
    await linkEventsToClient(clientId, client.name);
  };

  const handleOpenLinkDialog = () => {
    if (clients.length === 0) {
      setLinkError(t("coachingNavigator.coachingCalendar.noClientsFound"));
      return;
    }
    setIsLinkDialogOpen(true);
  };

  const handleRequestDisconnectClient = useCallback((eventId: string) => {
    setDisconnectEventId(eventId);
  }, []);

  const handleConfirmDisconnect = useCallback(async () => {
    if (!disconnectEventId || !creatorEmail) return;
    setIsUnlinking(true);
    try {
      const sessions = await sessionService.getByCreator(creatorEmail);
      const sessionByEventId = buildExistingSessionByEventId(sessions || []);
      const session = sessionByEventId.get(disconnectEventId);
      const sessionId = resolveSessionId(session);
      if (session && sessionId) {
        // Only unset clientId — calendarEventId must stay so the session can
        // be found and reused if the user re-links this event to any client.
        await sessionService.patch(sessionId, {
          clientId: null as unknown as string,
        });
      }
      setLinkedClientByEventId((prev) => {
        const next = { ...prev };
        delete next[disconnectEventId];
        return next;
      });
      setDisconnectEventId(null);
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "";
      setLinkError(
        msg ||
        t("coachingNavigator.coachingCalendar.failedToUnlinkEvent") ||
        "Failed to unlink event",
      );
      setDisconnectEventId(null);
    } finally {
      setIsUnlinking(false);
    }
  }, [disconnectEventId, creatorEmail, t]);

  // ── Render ─────────────────────────────────────────────────────────
  const connectionsSlot = (
    <Box>
      {/* ConnectCalendarDialog is rendered inside the toolbar slot */}
      <ConnectSlot
        connectLoading={connectLoading}
        connectError={connectError}
        connections={connections}
        setConnectError={setConnectError}
        handleConnectCalendar={handleConnectCalendar}
        handleDisconnectAccount={handleDisconnectAccount}
        connectionStatuses={connectionStatuses}
      />
    </Box>
  );

  const onEventClick = useCallback((event: CalendarEvent) => {
    setDetailEvent(event);
  }, []);

  return (
    <Box sx={{ position: "relative" }}>
      <MonthlyCalendarView
        events={filteredEvents}
        loading={loading}
        viewedMonth={viewedMonth}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        onSetMonth={setViewedMonth}
        availableMonths={availableMonths}
        searchText={searchText}
        onSearchChange={setSearchText}
        isSearchActive={isSearchActive}
        searchInProgress={searchInProgress}
        connectionsSlot={connectionsSlot}
        onRefresh={fetchAllEvents}
        onEventClick={onEventClick}
        onChevronClick={onChevronClickNavigate}
        enableConflictDetection={true}
        connectionStatuses={connectionStatuses}
        selection={{
          selectedIds,
          onToggle: handleSelectionChange,
          onToggleAllVisible: handleSelectAllVisible,
          linkedClientByEventId,
          onOpenLinkDialog: handleOpenLinkDialog,
          isLinking,
          onDisconnectClient: handleRequestDisconnectClient,
        }}
        title={t("calendar.yourCalendar", { defaultValue: "Your Calendar" })}
        includePastInSearch={includePastInSearch}
        onTogglePastInSearch={setIncludePastInSearch}
      />

      <LinkClientDialog
        open={isLinkDialogOpen}
        clients={clients}
        selectedCount={selectedIds.size}
        existingLinkages={Object.fromEntries(
          Array.from(selectedIds)
            .filter((id) => linkedClientByEventId[id])
            .map((id) => [id, linkedClientByEventId[id]]),
        )}
        onClose={() => setIsLinkDialogOpen(false)}
        onLink={handleClientSelected}
      />

      <ConfirmationPanel
        action="delete"
        showModal={disconnectEventId !== null}
        hideModal={() => {
          if (!isUnlinking) setDisconnectEventId(null);
        }}
        confirmModal={() => void handleConfirmDisconnect()}
        title={t("calendar.disconnectClientTitle", {
          defaultValue: "Disconnect Client",
        })}
        content={t("calendar.disconnectClientMessage", {
          clientName: disconnectEventId
            ? (linkedClientByEventId[disconnectEventId] ?? "")
            : "",
          defaultValue:
            "Are you sure you want to disconnect {{clientName}} from this meeting? You can reconnect a client at any time.",
        })}
        customActions={
          <>
            <Button
              variant="outlined"
              onClick={() => setDisconnectEventId(null)}
              disabled={isUnlinking}
            >
              {t("calendar.disconnectClientCancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={isUnlinking ? null : <DeleteIcon />}
              onClick={() => void handleConfirmDisconnect()}
              disabled={isUnlinking}
              autoFocus
            >
              {isUnlinking ? (
                <CircularProgress size={24} color="error" />
              ) : (
                t("calendar.disconnectClientConfirm", {
                  defaultValue: "Disconnect",
                })
              )}
            </Button>
          </>
        }
      />

      <Snackbar
        open={!!linkError}
        autoHideDuration={5000}
        onClose={() => setLinkError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          onClose={() => setLinkError(null)}
          sx={{ width: "100%" }}
        >
          {linkError}
        </Alert>
      </Snackbar>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "error" ? 8000 : 4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ── Event Details popup (double-click / Enter) — lovable design ── */}
      <MeetingDetailsDialog
        open={detailEvent !== null}
        event={detailEvent}
        onClose={() => setDetailEvent(null)}
      />
    </Box>
  );
};

// Wrapper so the connect dialog can be moved out of the toolbar code path.
import ConnectCalendarDialog from "../../connectCalendarDialog";
import type { CalendarProvider } from "../../../models";
import type { CalendarConnectionStatusMap } from "../../../util/calendarEvent";

interface ConnectSlotProps {
  connectLoading: boolean;
  connectError: string | null;
  connections: CalendarConnection[];
  setConnectError: (value: string | null) => void;
  handleConnectCalendar: (
    provider: CalendarProvider,
    includePrivateEvents: boolean,
  ) => Promise<boolean>;
  handleDisconnectAccount: (
    provider: string,
    accountUsername: string,
  ) => Promise<void>;
  connectionStatuses: CalendarConnectionStatusMap;
}

const ConnectSlot = ({
  connectLoading,
  connectError,
  connections,
  setConnectError,
  handleConnectCalendar,
  handleDisconnectAccount,
  connectionStatuses,
}: ConnectSlotProps) => (
  <ConnectCalendarDialog
    onConnect={handleConnectCalendar}
    loading={connectLoading}
    error={connectError}
    connections={connections}
    onDisconnect={handleDisconnectAccount}
    onOpenDialog={() => setConnectError(null)}
    connectionStatuses={connectionStatuses}
    outlookEnabled={true}
  />
);

export default EventList;
